/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { PagedModelTenderItemResponseModel } from '../models/paged-model-tender-item-response-model';
import { PagedModelTenderResponseModel } from '../models/paged-model-tender-response-model';
import { TenderDto } from '../models/tender-dto';
import { TenderItemResponseModel } from '../models/tender-item-response-model';
import { TenderPatchDto } from '../models/tender-patch-dto';
import { TenderResponseModel } from '../models/tender-response-model';

/**
 * Tenders endpoints.
 */
@Injectable({
  providedIn: 'root',
})
export class TendersService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /**
   * Path part for operation getTender
   */
  static readonly GetTenderPath = '/tenders/{tenderId}';

  /**
   * Gets a tender.
   *
   * Returns the specified tender.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getTender()` instead.
   *
   * This method doesn't expect any request body.
   */
  getTender$Response(
    params: {
      tenderId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<TenderResponseModel>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      TendersService.GetTenderPath,
      'get'
    );
    if (params) {
      rb.path('tenderId', params.tenderId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<TenderResponseModel>;
        })
      );
  }

  /**
   * Gets a tender.
   *
   * Returns the specified tender.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getTender$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getTender(
    params: {
      tenderId: string;
    },
    context?: HttpContext
  ): Observable<TenderResponseModel> {
    return this.getTender$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<TenderResponseModel>) =>
          r.body as TenderResponseModel
      )
    );
  }

  /**
   * Path part for operation updateTender
   */
  static readonly UpdateTenderPath = '/tenders/{tenderId}';

  /**
   * Updates a tender.
   *
   * Updates the specified tender.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateTender()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateTender$Response(
    params: {
      tenderId: string;
      body: TenderDto;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      TendersService.UpdateTenderPath,
      'put'
    );
    if (params) {
      rb.path('tenderId', params.tenderId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Updates a tender.
   *
   * Updates the specified tender.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateTender$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateTender(
    params: {
      tenderId: string;
      body: TenderDto;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.updateTender$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation patchTender
   */
  static readonly PatchTenderPath = '/tenders/{tenderId}';

  /**
   * Updates a tender.
   *
   * Updates the specified tender.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `patchTender()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  patchTender$Response(
    params: {
      tenderId: string;
      body: TenderPatchDto;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      TendersService.PatchTenderPath,
      'patch'
    );
    if (params) {
      rb.path('tenderId', params.tenderId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Updates a tender.
   *
   * Updates the specified tender.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `patchTender$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  patchTender(
    params: {
      tenderId: string;
      body: TenderPatchDto;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.patchTender$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getTenderItems
   */
  static readonly GetTenderItemsPath = '/tenders/{tenderId}/items';

  /**
   * Listing items of a tender.
   *
   * Returns the items of specified tender.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getTenderItems()` instead.
   *
   * This method doesn't expect any request body.
   */
  getTenderItems$Response(
    params: {
      tenderId: string;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PagedModelTenderItemResponseModel>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      TendersService.GetTenderItemsPath,
      'get'
    );
    if (params) {
      rb.path('tenderId', params.tenderId, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PagedModelTenderItemResponseModel>;
        })
      );
  }

  /**
   * Listing items of a tender.
   *
   * Returns the items of specified tender.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getTenderItems$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getTenderItems(
    params: {
      tenderId: string;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<PagedModelTenderItemResponseModel> {
    return this.getTenderItems$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PagedModelTenderItemResponseModel>) =>
          r.body as PagedModelTenderItemResponseModel
      )
    );
  }

  /**
   * Path part for operation addOrUpdateTenderItems
   */
  static readonly AddOrUpdateTenderItemsPath = '/tenders/{tenderId}/items';

  /**
   * Adds/update items to a tender.
   *
   * Adds/updates items of specified tender.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `addOrUpdateTenderItems()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  addOrUpdateTenderItems$Response(
    params: {
      tenderId: string;
      body: {
        [key: string]: number;
      };
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      TendersService.AddOrUpdateTenderItemsPath,
      'put'
    );
    if (params) {
      rb.path('tenderId', params.tenderId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Adds/update items to a tender.
   *
   * Adds/updates items of specified tender.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `addOrUpdateTenderItems$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  addOrUpdateTenderItems(
    params: {
      tenderId: string;
      body: {
        [key: string]: number;
      };
    },
    context?: HttpContext
  ): Observable<void> {
    return this.addOrUpdateTenderItems$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation deleteTenderItems
   */
  static readonly DeleteTenderItemsPath = '/tenders/{tenderId}/items';

  /**
   * Removes items from a tender.
   *
   * Removes the specified items from specified tender.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteTenderItems()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  deleteTenderItems$Response(
    params: {
      tenderId: string;
      body: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      TendersService.DeleteTenderItemsPath,
      'delete'
    );
    if (params) {
      rb.path('tenderId', params.tenderId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Removes items from a tender.
   *
   * Removes the specified items from specified tender.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteTenderItems$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  deleteTenderItems(
    params: {
      tenderId: string;
      body: Array<string>;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.deleteTenderItems$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getTenderItem
   */
  static readonly GetTenderItemPath = '/tenders/{tenderId}/items/{itemId}';

  /**
   * Gets an item of a tender.
   *
   * Returns the specified item of specified tender.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getTenderItem()` instead.
   *
   * This method doesn't expect any request body.
   */
  getTenderItem$Response(
    params: {
      tenderId: string;
      itemId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<TenderItemResponseModel>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      TendersService.GetTenderItemPath,
      'get'
    );
    if (params) {
      rb.path('tenderId', params.tenderId, {});
      rb.path('itemId', params.itemId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<TenderItemResponseModel>;
        })
      );
  }

  /**
   * Gets an item of a tender.
   *
   * Returns the specified item of specified tender.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getTenderItem$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getTenderItem(
    params: {
      tenderId: string;
      itemId: string;
    },
    context?: HttpContext
  ): Observable<TenderItemResponseModel> {
    return this.getTenderItem$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<TenderItemResponseModel>) =>
          r.body as TenderItemResponseModel
      )
    );
  }

  /**
   * Path part for operation updateTenderItem
   */
  static readonly UpdateTenderItemPath = '/tenders/{tenderId}/items/{itemId}';

  /**
   * Updates an item of a tender.
   *
   * Updates the specified item of specified tender.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateTenderItem()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateTenderItem$Response(
    params: {
      tenderId: string;
      itemId: string;
      body: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      TendersService.UpdateTenderItemPath,
      'put'
    );
    if (params) {
      rb.path('tenderId', params.tenderId, {});
      rb.path('itemId', params.itemId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Updates an item of a tender.
   *
   * Updates the specified item of specified tender.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateTenderItem$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateTenderItem(
    params: {
      tenderId: string;
      itemId: string;
      body: number;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.updateTenderItem$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation removeTenderItem
   */
  static readonly RemoveTenderItemPath = '/tenders/{tenderId}/items/{itemId}';

  /**
   * Remove an item from a tender.
   *
   * Removes the specified item from specified tender.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `removeTenderItem()` instead.
   *
   * This method doesn't expect any request body.
   */
  removeTenderItem$Response(
    params: {
      tenderId: string;
      itemId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      TendersService.RemoveTenderItemPath,
      'delete'
    );
    if (params) {
      rb.path('tenderId', params.tenderId, {});
      rb.path('itemId', params.itemId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Remove an item from a tender.
   *
   * Removes the specified item from specified tender.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `removeTenderItem$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  removeTenderItem(
    params: {
      tenderId: string;
      itemId: string;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.removeTenderItem$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation setTenderItemBid
   */
  static readonly SetTenderItemBidPath =
    '/tenders/{tenderId}/items/{itemId}/price';

  /**
   * Sets a bid on a tender item.
   *
   * Sets the bid of the specified item of specified tender.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `setTenderItemBid()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  setTenderItemBid$Response(
    params: {
      tenderId: string;
      itemId: string;
      body: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      TendersService.SetTenderItemBidPath,
      'put'
    );
    if (params) {
      rb.path('tenderId', params.tenderId, {});
      rb.path('itemId', params.itemId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Sets a bid on a tender item.
   *
   * Sets the bid of the specified item of specified tender.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `setTenderItemBid$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  setTenderItemBid(
    params: {
      tenderId: string;
      itemId: string;
      body: number;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.setTenderItemBid$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation revokeTenderItemBid
   */
  static readonly RevokeTenderItemBidPath =
    '/tenders/{tenderId}/items/{itemId}/price';

  /**
   * REvokes a bid on a tender item.
   *
   * Revokes the bid of the specified item of specified tender.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `revokeTenderItemBid()` instead.
   *
   * This method doesn't expect any request body.
   */
  revokeTenderItemBid$Response(
    params: {
      tenderId: string;
      itemId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      TendersService.RevokeTenderItemBidPath,
      'delete'
    );
    if (params) {
      rb.path('tenderId', params.tenderId, {});
      rb.path('itemId', params.itemId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * REvokes a bid on a tender item.
   *
   * Revokes the bid of the specified item of specified tender.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `revokeTenderItemBid$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  revokeTenderItemBid(
    params: {
      tenderId: string;
      itemId: string;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.revokeTenderItemBid$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation setTenderItemBid1
   */
  static readonly SetTenderItemBid1Path = '/tenders/{tenderId}/items/prices';

  /**
   * Sets a bid on a tender item.
   *
   * Sets the bid of the specified item of specified tender.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `setTenderItemBid1()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  setTenderItemBid1$Response(
    params: {
      tenderId: string;
      body: {
        [key: string]: number;
      };
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      TendersService.SetTenderItemBid1Path,
      'put'
    );
    if (params) {
      rb.path('tenderId', params.tenderId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Sets a bid on a tender item.
   *
   * Sets the bid of the specified item of specified tender.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `setTenderItemBid1$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  setTenderItemBid1(
    params: {
      tenderId: string;
      body: {
        [key: string]: number;
      };
    },
    context?: HttpContext
  ): Observable<void> {
    return this.setTenderItemBid1$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation revokeTenderItemBids
   */
  static readonly RevokeTenderItemBidsPath = '/tenders/{tenderId}/items/prices';

  /**
   * Revokes bids on a tender item.
   *
   * Revokes the bids of the specified items of specified tender.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `revokeTenderItemBids()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  revokeTenderItemBids$Response(
    params: {
      tenderId: string;
      body: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      TendersService.RevokeTenderItemBidsPath,
      'delete'
    );
    if (params) {
      rb.path('tenderId', params.tenderId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Revokes bids on a tender item.
   *
   * Revokes the bids of the specified items of specified tender.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `revokeTenderItemBids$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  revokeTenderItemBids(
    params: {
      tenderId: string;
      body: Array<string>;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.revokeTenderItemBids$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation listTenders
   */
  static readonly ListTendersPath = '/tenders';

  /**
   * Listing tenders.
   *
   * Lists available tenders. Only one of quert parameters could be set but not both of them.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listTenders()` instead.
   *
   * This method doesn't expect any request body.
   */
  listTenders$Response(
    params?: {
      /**
       * The merchantId which announced the tender.
       */
      announcerId?: string;

      /**
       * The target siteId of tender.
       */
      announcerSiteId?: string;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PagedModelTenderResponseModel>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      TendersService.ListTendersPath,
      'get'
    );
    if (params) {
      rb.query('announcerId', params.announcerId, {});
      rb.query('announcerSiteId', params.announcerSiteId, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PagedModelTenderResponseModel>;
        })
      );
  }

  /**
   * Listing tenders.
   *
   * Lists available tenders. Only one of quert parameters could be set but not both of them.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listTenders$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listTenders(
    params?: {
      /**
       * The merchantId which announced the tender.
       */
      announcerId?: string;

      /**
       * The target siteId of tender.
       */
      announcerSiteId?: string;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<PagedModelTenderResponseModel> {
    return this.listTenders$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PagedModelTenderResponseModel>) =>
          r.body as PagedModelTenderResponseModel
      )
    );
  }

  /**
   * Path part for operation createTender
   */
  static readonly CreateTenderPath = '/tenders';

  /**
   * Create a new tender.
   *
   * Creates a new tender
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createTender()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createTender$Response(
    params: {
      body: TenderDto;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<TenderResponseModel>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      TendersService.CreateTenderPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<TenderResponseModel>;
        })
      );
  }

  /**
   * Create a new tender.
   *
   * Creates a new tender
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createTender$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createTender(
    params: {
      body: TenderDto;
    },
    context?: HttpContext
  ): Observable<TenderResponseModel> {
    return this.createTender$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<TenderResponseModel>) =>
          r.body as TenderResponseModel
      )
    );
  }
}
